import { useState } from 'react'
import { motion } from 'framer-motion'
import { FileAudio, Plus, Trash2, Type, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { Node } from '@xyflow/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import type { IvrAvailableResources } from '@/api/ivrFlows'
import { useAIAgentMissions } from '@/api/aiAgents'
import { useProfile } from '@/api/profile'
import { useCompany } from '@/api/company'
import { getCategoryByNodeType, getNodeDefinition } from './nodeCategories'
import { AudioUploadField } from './AudioUploadField'
import { VoiceSelectContent } from './ttsVoices'
import { HeuresDuNoeud } from './HeuresDuNoeud'

interface IvrNodeData {
  label: string
  nodeType: string
  config: Record<string, unknown>
  [key: string]: unknown
}

interface Props {
  selectedNode: Node<IvrNodeData> | null
  onUpdateNode: (nodeId: string, data: Partial<IvrNodeData>) => void
  /** Merges a patch into the node's latest config. */
  onUpdateConfig: (nodeId: string, patch: Record<string, unknown>) => void
  onDeleteNode: (nodeId: string) => void
  onClose: () => void
  resources: IvrAvailableResources | undefined
  /** Le flux ouvert : pour savoir sur quel numéro il est branché. */
  flowId: number
}

/**
 * Panneau de configuration latéral droit — port 1:1 du PropertiesPanel Next.
 * S'affiche quand un nœud est sélectionné, avec un form par type de node.
 */
export function PropertiesPanel({
  selectedNode,
  onUpdateNode,
  onUpdateConfig,
  onDeleteNode,
  onClose,
  resources,
  flowId,
}: Props) {
  const { t } = useTranslation()

  if (!selectedNode) return null

  const { data, id } = selectedNode
  const category = getCategoryByNodeType(data.nodeType)
  const nodeDef = getNodeDefinition(data.nodeType)
  if (!category || !nodeDef) return null

  const teams = resources?.teams ?? []
  const agents = resources?.agents ?? []
  const aiAgents = resources?.ai_agents ?? []
  const phoneLists = resources?.phone_lists ?? []

  const updateConfig: UpdateFn = (key, value) => {
    const patch = typeof key === 'string' ? { [key]: value } : key
    onUpdateConfig(id, patch)
  }

  const dotColorMap: Record<string, string> = {
    'text-slate-600': 'bg-slate-400',
    'text-amber-600': 'bg-amber-400',
    'text-blue-600': 'bg-blue-400',
    'text-emerald-600': 'bg-emerald-400',
    'text-violet-600': 'bg-violet-400',
  }
  const dotColor = dotColorMap[category.color] || 'bg-gray-400'

  const isStart = data.nodeType === 'start'

  return (
    <motion.div
      initial={{ x: 320, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 320, opacity: 0 }}
      transition={{ type: 'spring', damping: 30, stiffness: 350 }}
      className="w-80 bg-[#131625] border-l border-white/[0.06] flex flex-col h-full"
    >
      {/* Header */}
      <div className="px-4 pt-5 pb-4 border-b border-white/[0.06]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className={cn('w-3 h-3 rounded-full', dotColor)} />
            <h3 className="text-[14px] font-semibold text-white/90 truncate">
              {data.label || t(nodeDef.labelKey)}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md text-white/30 hover:text-white/70 hover:bg-white/[0.06] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[11px] text-white/30 mt-1 ml-[22px]">
          {t(nodeDef.descKey)}
        </p>
      </div>

      {/* Label edit */}
      <div className="px-4 py-3 border-b border-white/[0.06]">
        <Label className="text-[10px] font-semibold text-white/30 uppercase tracking-[0.1em]">
          {t('manager.flows.editor.config.nodeName')}
        </Label>
        <Input
          value={data.label || ''}
          onChange={(e) => onUpdateNode(id, { label: e.target.value })}
          placeholder={t(nodeDef.labelKey)}
          className="mt-2 h-9 bg-white/[0.04] border-white/[0.08] text-white/90 placeholder:text-white/20 focus:border-white/20 focus:ring-0 rounded-lg text-sm"
        />
      </div>

      {/* Config fields */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Keyed by node: a form's local state (an upload in progress, an error) never shows on another node. */}
        <div
          key={id}
          className="
            [&_label]:text-white/60 [&_label]:text-[12px] [&_label]:font-medium
            [&_input]:bg-white/[0.04] [&_input]:border-white/[0.08] [&_input]:text-white/90 [&_input]:placeholder:text-white/20 [&_input]:rounded-lg
            [&_textarea]:bg-white/[0.04] [&_textarea]:border-white/[0.08] [&_textarea]:text-white/90 [&_textarea]:placeholder:text-white/20 [&_textarea]:rounded-lg
            [&_button[role=combobox]]:bg-white/[0.04] [&_button[role=combobox]]:border-white/[0.08] [&_button[role=combobox]]:text-white/90 [&_button[role=combobox]]:rounded-lg
            [&_.text-gray-500]:text-white/30 [&_.text-gray-600]:text-white/35 [&_.text-gray-400]:text-white/25
            [&_.bg-gray-50]:bg-white/[0.03]
            [&_.border]:border-white/[0.06]
            [&_.bg-gray-100]:bg-white/[0.04] [&_.bg-gray-200]:bg-white/[0.06]
            [&_.bg-white]:bg-white/[0.08]
            [&_.shadow-sm]:shadow-none
            [&_p]:text-white/50
          "
        >
          {/* Switch sur nodeType — port 1:1 du Next */}
          {data.nodeType === 'start' && (
            <div className="text-sm text-gray-500 italic">
              {t('manager.flows.editor.config.startNoConfig')}
            </div>
          )}

          {data.nodeType === 'hangup' && (
            <div className="text-sm text-gray-500 italic">
              {t('manager.flows.editor.config.hangupDesc')}
            </div>
          )}

          {data.nodeType === 'time_check' && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg border">
                <p className="text-sm text-gray-600">
                  {t('manager.flows.editor.config.timeCheckDescription')}
                </p>
              </div>
              <HeuresDuNoeud flowId={flowId} resources={resources} />
              <div>
                <Label>{t('manager.flows.editor.config.timezone')}</Label>
                <p className="text-xs text-gray-500 mb-1">
                  {t('manager.flows.editor.config.timezoneHint')}
                </p>
                <Select
                  value={
                    (data.config?.timezone as string) || 'America/Montreal'
                  }
                  onValueChange={(v) => updateConfig('timezone', v)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="America/Toronto">
                      Toronto (EST/EDT)
                    </SelectItem>
                    <SelectItem value="America/Montreal">
                      Montréal (EST/EDT)
                    </SelectItem>
                    <SelectItem value="America/Vancouver">
                      Vancouver (PST/PDT)
                    </SelectItem>
                    <SelectItem value="America/Halifax">
                      Halifax (AST/ADT)
                    </SelectItem>
                    <SelectItem value="America/Winnipeg">
                      Winnipeg (CST/CDT)
                    </SelectItem>
                    <SelectItem value="America/Edmonton">
                      Edmonton (MST/MDT)
                    </SelectItem>
                    <SelectItem value="America/St_Johns">
                      St. John's (NST/NDT)
                    </SelectItem>
                    <SelectItem value="America/New_York">
                      New York (EST/EDT)
                    </SelectItem>
                    <SelectItem value="America/Chicago">
                      Chicago (CST/CDT)
                    </SelectItem>
                    <SelectItem value="America/Denver">
                      Denver (MST/MDT)
                    </SelectItem>
                    <SelectItem value="America/Los_Angeles">
                      Los Angeles (PST/PDT)
                    </SelectItem>
                    <SelectItem value="America/Phoenix">
                      Phoenix (MST)
                    </SelectItem>
                    <SelectItem value="America/Anchorage">
                      Anchorage (AKST/AKDT)
                    </SelectItem>
                    <SelectItem value="Pacific/Honolulu">
                      Honolulu (HST)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {data.nodeType === 'caller_condition' && (
            <CallerConditionForm
              config={data.config}
              updateConfig={updateConfig}
              phoneLists={phoneLists}
            />
          )}

          {data.nodeType === 'called_number_condition' && (
            <CalledNumberConditionForm
              config={data.config}
              updateConfig={updateConfig}
              phoneLists={phoneLists}
            />
          )}

          {data.nodeType === 'language_select' && (
            <LanguageSelectForm
              config={data.config}
              updateConfig={updateConfig}
            />
          )}

          {data.nodeType === 'message' && (
            <MessageForm config={data.config} updateConfig={updateConfig} />
          )}

          {data.nodeType === 'sms' && (
            <SmsForm config={data.config} updateConfig={updateConfig} />
          )}

          {data.nodeType === 'menu' && (
            <MenuForm config={data.config} updateConfig={updateConfig} />
          )}

          {data.nodeType === 'route_team' && (
            <RouteTeamForm
              config={data.config}
              updateConfig={updateConfig}
              teams={teams}
            />
          )}

          {data.nodeType === 'queue' && (
            <QueueForm
              config={data.config}
              updateConfig={updateConfig}
              teams={teams}
            />
          )}

          {data.nodeType === 'route_agent' && (
            <RouteAgentForm
              config={data.config}
              updateConfig={updateConfig}
              agents={agents}
            />
          )}

          {data.nodeType === 'route_number' && (
            <RouteNumberForm
              config={data.config}
              updateConfig={updateConfig}
            />
          )}

          {data.nodeType === 'route_ai' && (
            <RouteAiForm
              config={data.config}
              updateConfig={updateConfig}
              aiAgents={aiAgents}
            />
          )}

          {data.nodeType === 'voicemail' && (
            <VoicemailForm config={data.config} updateConfig={updateConfig} />
          )}
        </div>
      </div>

      {/* Delete (sauf pour start) */}
      {!isStart && (
        <div className="p-4 border-t border-white/[0.06]">
          <button
            type="button"
            onClick={() => {
              onDeleteNode(id)
              onClose()
            }}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-[13px] font-medium text-red-400/80 bg-red-500/[0.06] border border-red-500/[0.1] hover:bg-red-500/[0.12] hover:text-red-400 hover:border-red-500/20 transition-all"
          >
            <Trash2 className="w-3.5 h-3.5" />
            {t('manager.flows.editor.config.deleteNode')}
          </button>
        </div>
      )}
    </motion.div>
  )
}

// =============================================================================
// FORMS PAR NODE TYPE
// =============================================================================

type Config = Record<string, unknown>
// A single key OR a multi-key patch. Each patch is merged into the node's latest
// config (onUpdateConfig), so successive calls no longer overwrite each other; one
// gesture that sets several keys still sends them as one patch.
type UpdateFn = (key: string | Record<string, unknown>, value?: unknown) => void

interface ConfigProps {
  config: Config
  updateConfig: UpdateFn
}

function CallerConditionForm({
  config,
  updateConfig,
  phoneLists,
}: ConfigProps & {
  phoneLists: IvrAvailableResources['phone_lists']
}) {
  const { t } = useTranslation()
  const conditionType = (config?.condition_type as string) || 'prefix'

  return (
    <div className="space-y-4">
      <div>
        <Label>{t('manager.flows.editor.config.conditionType')}</Label>
        <Select
          value={conditionType}
          onValueChange={(v) => updateConfig('condition_type', v)}
        >
          <SelectTrigger className="mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="prefix">
              {t('manager.flows.editor.config.conditionPrefix')}
            </SelectItem>
            <SelectItem value="phone_list">
              {t('manager.flows.editor.config.conditionPhoneList')}
            </SelectItem>
            <SelectItem value="international">
              {t('manager.flows.editor.config.conditionInternational')}
            </SelectItem>
            <SelectItem value="local">
              {t('manager.flows.editor.config.conditionLocal')}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {conditionType === 'prefix' && (
        <div>
          <Label>{t('manager.flows.editor.config.prefix')}</Label>
          <Input
            value={(config?.value as string) || ''}
            onChange={(e) => updateConfig('value', e.target.value)}
            placeholder={t('manager.flows.editor.config.prefixPlaceholder')}
            className="mt-1"
          />
        </div>
      )}

      {conditionType === 'phone_list' && (
        <div>
          <Label>{t('manager.flows.editor.config.phoneList')}</Label>
          <Select
            value={String(config?.phone_list_id || '')}
            onValueChange={(v) =>
              updateConfig('phone_list_id', v ? parseInt(v) : null)
            }
          >
            <SelectTrigger className="mt-1">
              <SelectValue
                placeholder={t('manager.flows.editor.config.selectList')}
              />
            </SelectTrigger>
            <SelectContent>
              {phoneLists.map((pl) => (
                <SelectItem key={pl.id} value={String(pl.id)}>
                  {pl.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  )
}

function CalledNumberConditionForm({
  config,
  updateConfig,
  phoneLists,
}: ConfigProps & {
  phoneLists: IvrAvailableResources['phone_lists']
}) {
  const { t } = useTranslation()
  return (
    <div className="space-y-4">
      <div>
        <Label>{t('manager.flows.editor.config.calledPhoneList')}</Label>
        <p className="text-xs text-gray-500 mb-2">
          {t('manager.flows.editor.config.calledPhoneListHint')}
        </p>
        <Select
          value={String((config?.phone_list_id as number) || '')}
          onValueChange={(v) =>
            updateConfig('phone_list_id', v ? parseInt(v) : null)
          }
        >
          <SelectTrigger className="mt-1">
            <SelectValue
              placeholder={t('manager.flows.editor.config.selectList')}
            />
          </SelectTrigger>
          <SelectContent>
            {phoneLists.map((pl) => (
              <SelectItem key={pl.id} value={String(pl.id)}>
                {pl.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}

interface LanguageItem {
  key: string
  voice?: string
  label: string
  prompt: string
  prompt_mode?: string
  prompt_audio_url?: string
  prompt_audio_filename?: string
}

function LanguageSelectForm({ config, updateConfig }: ConfigProps) {
  const { t } = useTranslation()
  const languages = (config?.languages as LanguageItem[]) || []

  const updateLanguage = (index: number, patch: Partial<LanguageItem>) => {
    const updated = [...languages]
    updated[index] = { ...updated[index], ...patch }
    updateConfig('languages', updated)
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>{t('manager.flows.editor.config.availableLanguages')}</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              const newKey = String(languages.length + 1)
              updateConfig('languages', [
                ...languages,
                {
                  key: newKey,
                  voice: 'Google.fr-CA-Neural2-A',
                  label: t('manager.flows.editor.config.newLanguage'),
                  prompt: '',
                  prompt_mode: 'tts',
                },
              ])
            }}
          >
            <Plus className="w-3 h-3 mr-1" />
            {t('manager.flows.editor.config.add')}
          </Button>
        </div>

        <div className="space-y-3">
          {languages.map((lang, index) => {
            const mode = lang.prompt_mode || 'tts'
            return (
              <div
                key={index}
                className="p-3 bg-gray-50 rounded-lg border space-y-2"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-500">
                    {t('manager.flows.editor.config.languageN', {
                      index: index + 1,
                    })}{' '}
                    - {lang.label || t('manager.flows.editor.config.unnamed')}
                  </span>
                  {languages.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        updateConfig(
                          'languages',
                          languages.filter((_, i) => i !== index),
                        )
                      }
                      className="h-6 w-6 p-0 text-red-500 hover:text-red-600"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">
                      {t('manager.flows.editor.config.dtmfKey')}
                    </Label>
                    <Input
                      value={lang.key}
                      onChange={(e) =>
                        updateLanguage(index, { key: e.target.value })
                      }
                      placeholder="1"
                      className="mt-1 h-8"
                      maxLength={1}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">
                      {t('manager.flows.editor.config.label')}
                    </Label>
                    <Input
                      value={lang.label}
                      onChange={(e) =>
                        updateLanguage(index, { label: e.target.value })
                      }
                      placeholder={t('manager.flows.editor.config.label')}
                      className="mt-1 h-8"
                    />
                  </div>
                </div>

                <ModeSwitcher
                  mode={mode}
                  onChange={(m) => updateLanguage(index, { prompt_mode: m })}
                />

                {mode === 'tts' ? (
                  <>
                    <div className="mb-2">
                      <Label className="text-xs">
                        {t('manager.flows.editor.config.ttsVoice')}
                      </Label>
                      <Select
                        value={lang.voice || ''}
                        onValueChange={(v) =>
                          updateLanguage(index, { voice: v })
                        }
                      >
                        <SelectTrigger className="mt-1 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <VoiceSelectContent actuelle={lang.voice} />
                      </Select>
                    </div>
                    <Textarea
                      value={lang.prompt || ''}
                      onChange={(e) =>
                        updateLanguage(index, { prompt: e.target.value })
                      }
                      placeholder={t(
                        'manager.flows.editor.config.langPromptDefault',
                        { key: lang.key },
                      )}
                      className="text-sm"
                      rows={2}
                    />
                  </>
                ) : (
                  <AudioUploadField
                    audioUrl={lang.prompt_audio_url || ''}
                    audioFilename={lang.prompt_audio_filename || ''}
                    onUpload={(url, filename) =>
                      updateLanguage(index, {
                        prompt_audio_url: url,
                        prompt_audio_filename: filename,
                      })
                    }
                    onClear={() =>
                      updateLanguage(index, {
                        prompt_audio_url: '',
                        prompt_audio_filename: '',
                      })
                    }
                  />
                )}
              </div>
            )
          })}
        </div>

        <p className="text-xs text-gray-400 mt-2">
          {t('manager.flows.editor.config.languageOrderHint')}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">
            {t('manager.flows.editor.config.timeoutSec')}
          </Label>
          <ChampNombre
            valeur={config?.timeout as number | undefined}
            defaut={20}
            min={3}
            max={30}
            onValider={(n) => updateConfig('timeout', n)}
            className="mt-1 h-8"
          />
        </div>
        <div>
          <Label className="text-xs">
            {t('manager.flows.editor.config.maxAttempts')}
          </Label>
          <ChampNombre
            valeur={config?.max_attempts as number | undefined}
            defaut={3}
            min={1}
            max={5}
            onValider={(n) => updateConfig('max_attempts', n)}
            className="mt-1 h-8"
          />
        </div>
      </div>
      <p className="text-xs text-gray-400">
        {t('manager.flows.editor.config.timeoutInvalidHint')}
      </p>
    </div>
  )
}

function MessageForm({ config, updateConfig }: ConfigProps) {
  const { t } = useTranslation()
  const mode = (config?.mode as string) || 'tts'

  return (
    <div className="space-y-4">
      <ModeSwitcher mode={mode} onChange={(m) => updateConfig('mode', m)} />

      {mode === 'tts' ? (
        <>
          <div>
            <Label>{t('manager.flows.editor.config.messageLabel')}</Label>
            <Textarea
              value={(config?.text as string) || ''}
              onChange={(e) => updateConfig('text', e.target.value)}
              placeholder={t(
                'manager.flows.editor.config.messagePlaceholder',
              )}
              className="mt-1"
              rows={4}
            />
          </div>
          <div>
            <Label>{t('manager.flows.editor.config.ttsVoice')}</Label>
            <Select
              value={(config?.voice as string) || 'Google.fr-CA-Neural2-A'}
              onValueChange={(v) => updateConfig('voice', v)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <VoiceSelectContent actuelle={config?.voice as string} />
            </Select>
          </div>
        </>
      ) : (
        <AudioUploadField
          audioUrl={(config?.audio_url as string) || ''}
          audioFilename={(config?.audio_filename as string) || ''}
          onUpload={(url, filename) => updateConfig({ audio_url: url, audio_filename: filename })}
          onClear={() => updateConfig({ audio_url: '', audio_filename: '' })}
        />
      )}
    </div>
  )
}

function SmsForm({ config, updateConfig }: ConfigProps) {
  const { t } = useTranslation()
  const to = (config?.to as string) || 'caller'
  const text = (config?.text as string) || ''

  // Twilio facture par tranche de 153 caractères en multi-segments. Un
  // manager qui écrit un pavé ne voit pas qu'il triple sa facture et qu'il
  // arrive découpé chez le destinataire — d'où le compteur explicite.
  const segments = text.length === 0 ? 0 : Math.ceil(text.length / 153)

  return (
    <div className="space-y-4">
      <div>
        <Label>{t('manager.flows.editor.config.smsTo')}</Label>
        <Select value={to} onValueChange={(v) => updateConfig('to', v)}>
          <SelectTrigger className="mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="caller">
              {t('manager.flows.editor.config.smsToCaller')}
            </SelectItem>
            <SelectItem value="custom">
              {t('manager.flows.editor.config.smsToCustom')}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {to === 'custom' && (
        <div>
          <Label>{t('manager.flows.editor.config.smsCustomNumber')}</Label>
          <Input
            value={(config?.custom_number as string) || ''}
            onChange={(e) => updateConfig('custom_number', e.target.value)}
            placeholder="+15145550199"
            className="mt-1"
          />
        </div>
      )}

      <div>
        <Label>{t('manager.flows.editor.config.smsText')}</Label>
        <Textarea
          value={text}
          onChange={(e) => updateConfig('text', e.target.value)}
          placeholder={t('manager.flows.editor.config.smsTextPlaceholder')}
          className="mt-1"
          rows={4}
        />
        <p className="mt-1 text-xs text-white/40">
          {t('manager.flows.editor.config.smsVariables')}
        </p>
        {segments > 1 && (
          <p className="mt-1 text-xs text-amber-400/80">
            {t('manager.flows.editor.config.smsSegments', {
              count: segments,
              chars: text.length,
            })}
          </p>
        )}
      </div>
    </div>
  )
}

function MenuForm({ config, updateConfig }: ConfigProps) {
  const { t } = useTranslation()
  const options = (config?.options as Array<{ key: string; label: string }>) || []
  const mode = (config?.message_mode as string) || 'tts'

  return (
    <div className="space-y-4">
      <Label>{t('manager.flows.editor.config.menuMessage')}</Label>
      <ModeSwitcher
        mode={mode}
        onChange={(m) => updateConfig('message_mode', m)}
      />

      {mode === 'tts' ? (
        <>
          <div>
            <Textarea
              value={(config?.message as string) || ''}
              onChange={(e) => updateConfig('message', e.target.value)}
              placeholder={t('manager.flows.editor.config.menuPlaceholder')}
              rows={3}
            />
          </div>
          <div>
            <Label>{t('manager.flows.editor.config.ttsVoice')}</Label>
            <Select
              value={(config?.voice as string) || 'Google.fr-CA-Neural2-A'}
              onValueChange={(v) => updateConfig('voice', v)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <VoiceSelectContent actuelle={config?.voice as string} />
            </Select>
          </div>
        </>
      ) : (
        <AudioUploadField
          audioUrl={(config?.message_audio_url as string) || ''}
          audioFilename={(config?.message_audio_filename as string) || ''}
          onUpload={(url, filename) =>
            updateConfig({ message_audio_url: url, message_audio_filename: filename })
          }
          onClear={() => updateConfig({ message_audio_url: '', message_audio_filename: '' })}
        />
      )}

      <div>
        <Label>{t('manager.flows.editor.config.options')}</Label>
        <div className="space-y-2 mt-2">
          {options.map((opt, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input
                value={opt.key}
                onChange={(e) => {
                  const next = [...options]
                  next[index] = { ...opt, key: e.target.value }
                  updateConfig('options', next)
                }}
                className="w-16"
                placeholder="1"
              />
              <Input
                value={opt.label}
                onChange={(e) => {
                  const next = [...options]
                  next[index] = { ...opt, label: e.target.value }
                  updateConfig('options', next)
                }}
                className="flex-1"
                placeholder={t('manager.flows.editor.config.label')}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() =>
                  updateConfig('options', options.filter((_, i) => i !== index))
                }
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              const newKey = String(options.length + 1)
              updateConfig('options', [...options, { key: newKey, label: '' }])
            }}
          >
            <Plus className="w-4 h-4 mr-1" />
            {t('manager.flows.editor.config.addOption')}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>{t('manager.flows.editor.config.timeoutSec')}</Label>
          <ChampNombre
            valeur={config?.timeout as number | undefined}
            defaut={20}
            min={3}
            max={30}
            onValider={(n) => updateConfig('timeout', n)}
            className="mt-1"
          />
        </div>
        <div>
          <Label>{t('manager.flows.editor.config.maxAttempts')}</Label>
          <ChampNombre
            valeur={config?.max_attempts as number | undefined}
            defaut={3}
            min={1}
            max={5}
            onValider={(n) => updateConfig('max_attempts', n)}
            className="mt-1"
          />
        </div>
      </div>
      <p className="text-xs text-gray-400">{t('manager.flows.editor.config.menuDelaiHint')}</p>
      <p className="text-xs text-gray-400">
        {t('manager.flows.editor.config.connectOutputsHint')}
      </p>
    </div>
  )
}

/** Attentes proposées, en secondes : chaque minute d'attente est une minute d'appel. */
const ATTENTES = [60, 120, 180, 300]

/** Ce que vit l'appelant qui attend : durée maximale, message, musique. */
function AttenteFields({ config, updateConfig }: ConfigProps) {
  const { t } = useTranslation()
  const attente = String(config?.max_wait || 120)

  return (
    <>
      <div>
        <Label>{t('manager.flows.editor.config.queueMaxWait')}</Label>
        <Select value={attente} onValueChange={(v) => updateConfig('max_wait', parseInt(v))}>
          <SelectTrigger className="mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ATTENTES.map((s) => (
              <SelectItem key={s} value={String(s)}>
                {t('manager.flows.editor.config.queueMinutes', { count: s / 60 })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>{t('manager.flows.editor.config.queueMessage')}</Label>
        <Textarea
          value={(config?.message as string) || ''}
          onChange={(e) => updateConfig('message', e.target.value)}
          className="mt-1 text-sm"
          rows={3}
        />
      </div>

      <div>
        <Label>{t('manager.flows.editor.config.queueMusic')}</Label>
        <div className="mt-1">
          <AudioUploadField
            audioUrl={(config?.audio_url as string) || ''}
            audioFilename={(config?.audio_filename as string) || ''}
            onUpload={(url, filename) => updateConfig({ audio_url: url, audio_filename: filename })}
            onClear={() => updateConfig({ audio_url: '', audio_filename: '' })}
          />
        </div>
      </div>
    </>
  )
}

function QueueForm({
  config,
  updateConfig,
  teams,
}: ConfigProps & { teams: IvrAvailableResources['teams'] }) {
  const { t } = useTranslation()

  return (
    <div className="space-y-4">
      <div>
        <Label>{t('manager.flows.editor.config.team')}</Label>
        <Select
          value={String(config?.team_id || '')}
          onValueChange={(v) =>
            updateConfig('team_id', v === 'all' ? 'all' : v ? parseInt(v) : null)
          }
        >
          <SelectTrigger className="mt-1">
            <SelectValue placeholder={t('manager.flows.editor.config.selectTeam')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('manager.flows.editor.config.teamAll', "Toute l'équipe")}</SelectItem>
            {teams.map((tm) => (
              <SelectItem key={tm.id} value={String(tm.id)}>
                {tm.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>{t('manager.flows.editor.config.ringTimeout')}</Label>
        <ChampNombre
          valeur={config?.ring_timeout as number | undefined}
          defaut={30}
          min={5}
          max={60}
          onValider={(n) => updateConfig('ring_timeout', n)}
          className="mt-1"
        />
      </div>

      <AttenteFields config={config} updateConfig={updateConfig} />
    </div>
  )
}

function RouteTeamForm({
  config,
  updateConfig,
  teams,
}: ConfigProps & { teams: IvrAvailableResources['teams'] }) {
  const { t } = useTranslation()
  const strategy = (config?.ring_strategy as string) || 'PARALLEL'

  return (
    <div className="space-y-4">
      <div>
        <Label>{t('manager.flows.editor.config.team')}</Label>
        <Select
          value={String(config?.team_id || '')}
          onValueChange={(v) =>
            updateConfig('team_id', v === 'all' ? 'all' : v ? parseInt(v) : null)
          }
        >
          <SelectTrigger className="mt-1">
            <SelectValue
              placeholder={t('manager.flows.editor.config.selectTeam')}
            />
          </SelectTrigger>
          <SelectContent>
            {/* « Toute l'équipe » : tous les agents et gestionnaires, sans équipe précise */}
            <SelectItem value="all">{t('manager.flows.editor.config.teamAll', "Toute l'équipe")}</SelectItem>
            {teams.map((tm) => (
              <SelectItem key={tm.id} value={String(tm.id)}>
                {tm.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>{t('manager.flows.editor.config.ringStrategy')}</Label>
        <Select
          value={strategy}
          onValueChange={(v) => updateConfig('ring_strategy', v)}
        >
          <SelectTrigger className="mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="PARALLEL">
              {t('manager.flows.editor.config.ringParallel')}
            </SelectItem>
            <SelectItem value="SEQUENTIAL">
              {t('manager.flows.editor.config.ringSequential')}
            </SelectItem>
            <SelectItem value="RANDOM">
              {t('manager.flows.editor.config.ringRandom')}
            </SelectItem>
            <SelectItem value="ROUND_ROBIN">
              {t('manager.flows.editor.config.ringRoundRobin')}
            </SelectItem>
            <SelectItem value="LEAST_RECENT">
              {t('manager.flows.editor.config.ringLeastRecent')}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>{t('manager.flows.editor.config.ringTimeout')}</Label>
        <ChampNombre
          valeur={config?.ring_timeout as number | undefined}
          defaut={20}
          min={5}
          max={60}
          onValider={(n) => updateConfig('ring_timeout', n)}
          className="mt-1"
        />
        <p className="text-xs text-gray-500 mt-1">
          {strategy === 'PARALLEL'
            ? t('manager.flows.editor.config.ringTimeoutHintParallel')
            : t('manager.flows.editor.config.ringTimeoutHintSequential')}
        </p>
      </div>

      <div className="flex items-center justify-between">
        <Label>{t('manager.flows.editor.config.teamWait')}</Label>
        <Switch
          checked={Boolean(config?.patienter)}
          onCheckedChange={(v) =>
            updateConfig(
              v && !config?.message
                ? { patienter: v, message: t('manager.flows.editor.config.teamWaitMessage') }
                : { patienter: v },
            )
          }
        />
      </div>
      {Boolean(config?.patienter) && <AttenteFields config={config} updateConfig={updateConfig} />}
    </div>
  )
}

function RouteAgentForm({
  config,
  updateConfig,
  agents,
}: ConfigProps & { agents: IvrAvailableResources['agents'] }) {
  const { t } = useTranslation()
  const { data: profile } = useProfile()
  // A step saved without its own ring time rings for the company's duration (server rule).
  const { data: company } = useCompany(profile?.company)
  return (
    <div className="space-y-4">
      <div>
        <Label>{t('manager.flows.editor.config.agent')}</Label>
        <Select
          value={String(config?.agent_id || '')}
          onValueChange={(v) =>
            updateConfig('agent_id', v ? parseInt(v) : null)
          }
        >
          <SelectTrigger className="mt-1">
            <SelectValue
              placeholder={t('manager.flows.editor.config.selectAgent')}
            />
          </SelectTrigger>
          <SelectContent>
            {agents.map((a) => (
              <SelectItem key={a.id} value={String(a.id)}>
                {a.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>{t('manager.flows.editor.config.ringTimeout')}</Label>
        <ChampNombre
          valeur={config?.ring_timeout as number | undefined}
          defaut={company?.ring_timeout ?? 40}
          min={5}
          max={60}
          onValider={(n) => updateConfig('ring_timeout', n)}
          className="mt-1"
        />
      </div>
      <p className="text-xs text-gray-400">
        {t('manager.flows.editor.config.noAnswerHint')}
      </p>
    </div>
  )
}

function RouteNumberForm({ config, updateConfig }: ConfigProps) {
  const { t } = useTranslation()
  return (
    <div className="space-y-4">
      <div>
        <Label>{t('manager.flows.editor.config.phoneNumber')}</Label>
        <Input
          value={(config?.phone_number as string) || ''}
          onChange={(e) => updateConfig('phone_number', e.target.value)}
          placeholder="+1 514 555 1234"
          className="mt-1"
        />
        <p className="text-xs text-gray-400 mt-1">
          {t('manager.flows.editor.config.phoneFormatHint')}
        </p>
      </div>
      <div>
        <Label>{t('manager.flows.editor.config.callerId')}</Label>
        <Select
          value={(config?.caller_id as string) || 'company'}
          onValueChange={(v) => updateConfig('caller_id', v)}
        >
          <SelectTrigger className="mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="company">
              {t('manager.flows.editor.config.callerIdCompany')}
            </SelectItem>
            <SelectItem value="caller">
              {t('manager.flows.editor.config.callerIdCaller')}
            </SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-gray-400 mt-1">
          {t('manager.flows.editor.config.callerIdHint')}
        </p>
      </div>
      <p className="text-xs text-gray-400">
        {t('manager.flows.editor.config.transferFailHint')}
      </p>
    </div>
  )
}

function RouteAiForm({
  config,
  updateConfig,
  aiAgents,
}: ConfigProps & { aiAgents: IvrAvailableResources['ai_agents'] }) {
  const { t } = useTranslation()
  const { data: profile } = useProfile()
  const agentId = Number(config?.ai_agent_id) || undefined
  // Missions de l'agent sélectionné — la branche peut confier une mission
  // précise à l'agent (« Marc est absent, prends un message ») au lieu de
  // le laisser dans son comportement par défaut.
  const { data: missions = [] } = useAIAgentMissions(profile?.company, agentId)
  const activeMissions = missions.filter((m) => m.is_active)
  const selectedMission = activeMissions.find(
    (m) => m.id === Number(config?.mission_id),
  )

  return (
    <div className="space-y-4">
      <div>
        <Label>{t('manager.flows.editor.config.aiAgent')}</Label>
        <Select
          value={String(config?.ai_agent_id || '')}
          onValueChange={(v) => {
            // Un SEUL patch : des appels séparés s'écraseraient entre eux
            // (chacun repart du config capturé au rendu).
            updateConfig({
              ai_agent_id: v ? parseInt(v) : null,
              // Changer d'agent invalide la mission (elle appartient à l'agent)
              mission_id: null,
              mission_variables: {},
            })
          }}
        >
          <SelectTrigger className="mt-1">
            <SelectValue
              placeholder={t('manager.flows.editor.config.selectAiAgent')}
            />
          </SelectTrigger>
          <SelectContent>
            {aiAgents.map((a) => (
              <SelectItem key={a.id} value={String(a.id)}>
                {a.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {agentId && activeMissions.length > 0 && (
        <div>
          <Label>{t('manager.flows.editor.config.aiMission')}</Label>
          <Select
            value={config?.mission_id ? String(config.mission_id) : 'none'}
            onValueChange={(v) => {
              // Un SEUL patch — deux appels séparés : le second écrasait
              // mission_id (c'était le bug « la mission revient toujours
              // au comportement par défaut »).
              updateConfig({
                mission_id: v === 'none' ? null : parseInt(v),
                mission_variables: {},
              })
            }}
          >
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">
                {t('manager.flows.editor.config.noMission')}
              </SelectItem>
              {activeMissions.map((m) => (
                <SelectItem key={m.id} value={String(m.id)}>
                  {m.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="mt-1 text-[11px] text-white/40">
            {t('manager.flows.editor.config.missionHint')}
          </p>
        </div>
      )}

      {/* Une même mission sert plusieurs branches ; c'est cette phrase qui
          les distingue (« Marc est absent toute la semaine »). Facultative :
          sans elle la mission s'exécute telle qu'elle est écrite. */}
      {selectedMission && (
        <div>
          <Label>{t('manager.flows.editor.config.missionContext')}</Label>
          <Textarea
            value={(config?.mission_context as string) || ''}
            onChange={(e) => updateConfig('mission_context', e.target.value)}
            rows={2}
            placeholder={t(
              'manager.flows.editor.config.missionContextPlaceholder',
            )}
            className="mt-1 text-sm"
          />
          <p className="mt-1 text-[11px] text-white/40">
            {t('manager.flows.editor.config.missionContextHint')}
          </p>
        </div>
      )}
    </div>
  )
}

function VoicemailForm({ config, updateConfig }: ConfigProps) {
  const { t } = useTranslation()
  const mode = (config?.greeting_mode as string) || 'tts'

  return (
    <div className="space-y-4">
      <Label>{t('manager.flows.editor.config.greetingMessage')}</Label>
      <ModeSwitcher
        mode={mode}
        onChange={(m) => updateConfig('greeting_mode', m)}
      />

      {mode === 'tts' ? (
        <>
          <div>
            <Textarea
              value={(config?.greeting as string) || ''}
              onChange={(e) => updateConfig('greeting', e.target.value)}
              placeholder={t('manager.flows.editor.config.voicemailPlaceholder')}
              rows={3}
            />
          </div>
          <div>
            <Label>{t('manager.flows.editor.config.ttsVoice')}</Label>
            <Select
              value={(config?.voice as string) || 'Google.fr-CA-Neural2-A'}
              onValueChange={(v) => updateConfig('voice', v)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <VoiceSelectContent actuelle={config?.voice as string} />
            </Select>
          </div>
        </>
      ) : (
        <AudioUploadField
          audioUrl={(config?.greeting_audio_url as string) || ''}
          audioFilename={(config?.greeting_audio_filename as string) || ''}
          onUpload={(url, filename) =>
            updateConfig({ greeting_audio_url: url, greeting_audio_filename: filename })
          }
          onClear={() => updateConfig({ greeting_audio_url: '', greeting_audio_filename: '' })}
        />
      )}

      <div>
        <Label>{t('manager.flows.editor.config.maxDuration')}</Label>
        <ChampNombre
          valeur={config?.max_duration as number | undefined}
          defaut={120}
          min={10}
          max={300}
          onValider={(n) => updateConfig('max_duration', n)}
          className="mt-1"
        />
      </div>
      <div className="flex items-center justify-between">
        <Label>{t('manager.flows.editor.config.transcribeMessage')}</Label>
        <Switch
          checked={(config?.transcribe as boolean) ?? true}
          onCheckedChange={(v) => updateConfig('transcribe', v)}
        />
      </div>
    </div>
  )
}

function ModeSwitcher({
  mode,
  onChange,
}: {
  mode: string
  onChange: (mode: string) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
      <button
        type="button"
        onClick={() => onChange('tts')}
        className={cn(
          'flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all',
          mode === 'tts'
            ? 'bg-white/[0.1] text-white'
            : 'text-white/40 hover:text-white/80',
        )}
      >
        <Type className="h-4 w-4" />
        {t('manager.flows.editor.config.textTts')}
      </button>
      <button
        type="button"
        onClick={() => onChange('audio')}
        className={cn(
          'flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all',
          mode === 'audio'
            ? 'bg-white/[0.1] text-white'
            : 'text-white/40 hover:text-white/80',
        )}
      >
        <FileAudio className="h-4 w-4" />
        {t('manager.flows.editor.config.audioFile')}
      </button>
    </div>
  )
}

/**
 * Champ numérique qu'on peut vider et retaper librement : la valeur n'est
 * enregistrée que si c'est un nombre, et remise dans les bornes (ou au défaut
 * si le champ reste vide) quand on le quitte.
 */
function ChampNombre({
  valeur,
  defaut,
  min,
  max,
  onValider,
  className,
}: {
  valeur: number | undefined
  defaut: number
  min: number
  max: number
  onValider: (n: number) => void
  className?: string
}) {
  const [texte, setTexte] = useState<string | null>(null)
  const affiche = texte ?? String(valeur ?? defaut)
  return (
    <Input
      type="text"
      inputMode="numeric"
      value={affiche}
      onChange={(e) => {
        const brut = e.target.value.replace(/\D/g, '').slice(0, 4)
        setTexte(brut)
        if (brut) onValider(Number(brut))
      }}
      onBlur={() => {
        const n = texte === null ? (valeur ?? defaut) : texte ? Number(texte) : defaut
        onValider(Math.min(max, Math.max(min, n)))
        setTexte(null)
      }}
      className={className}
    />
  )
}
